import delve from 'dlv'
import React from 'react'
import { Seo, SeoProvider } from 'components/shared/Seo'
import { buildStaticPaths, getDataDependencies } from 'services/api'

import fetchAllSlugs from 'src/lib/Strapi/fetchAllSlugs'
import fetchSingleType from 'src/lib/Strapi/fetchSingleType'
import fetchContentType from 'src/lib/Strapi/fetchContentType'

import ArticleContent from 'components/pages/blog/ArticleContent'
import BlogNav from 'components/shared/BlogNav'
import ProgressProvider from 'components/shared/ProgressProvider'
import SliceManager from 'components/shared/SliceManager'
import PreviewBanner from 'src/components/shared/PreviewBanner'
import spreadStrapiData from 'src/lib/Strapi/spreadStrapiData'

const Article = ({ pageData, preview }) => {
  /**
   * If seo exists on page data,
   * then add it as a default
   */
  const seo = {
    ...(pageData.seo && { ...pageData.seo }),
    cardStyle: 'summary_large_image',
    ogType: 'article',
  }

  const slices = delve(pageData, 'slices')

  return (
    <SeoProvider pageSeo={seo}>
      <Seo />
      <ProgressProvider>
        {preview && <PreviewBanner />}
        <BlogNav
          categories={delve(pageData, 'blogPostNavCategories')}
          newsletter={delve(pageData, 'newsletter')}
        />
        {pageData && (
          <>
            <ArticleContent
              showAuthorBlock
              showQuickInfos
              theme="purple"
              introText={delve(pageData, 'description')}
              featuredCategory={delve(pageData, 'featuredCategory')}
              title={
                delve(pageData, 'title') || delve(pageData.seo, 'metaTitle')
              }
              metaDescription={delve(pageData.seo, 'metaDescription')}
              image={delve(pageData, 'image')}
              author={{
                name: delve(pageData.user, 'data.attributes.username'),
                description: delve(
                  pageData.user,
                  'data.attributes.description'
                ),
                job: delve(pageData.user, 'data.attributes.job'),
                image: delve(pageData.user, 'data.attributes.avatar'),
                slug: delve(pageData.user, 'data.attributes.slug'),
              }}
              coauthors={
                pageData?.coauthors?.data.length > 0
                  ? delve(pageData, 'coauthors.data').map(
                      ({ id, attributes }) => ({
                        name: attributes.username,
                        description: attributes.description,
                        job: attributes.job,
                        image: attributes.avatar,
                        slug: attributes.slug,
                        id,
                      })
                    )
                  : []
              }
              publishedAt={delve(pageData, 'publishedAt')}
              updatedAt={delve(pageData, 'updatedAt')}
              content={delve(pageData, 'content')}
              // card={delve(pageData, 'card') ?? blogCardSingleType?.Card}
              version={delve(pageData, 'version')}
              level={delve(pageData, 'level')}
              tags={delve(pageData, 'tags')}
            />
          </>
        )}
        {slices && <SliceManager slices={slices} />}
      </ProgressProvider>
    </SeoProvider>
  )
}

export async function getStaticPaths() {
  const blogs = await fetchAllSlugs('blog-posts')
  const categories = await fetchAllSlugs('post-categories')
  const tags = await fetchAllSlugs('post-tags')

  const data = [
    ...blogs,
    ...categories.map((item) => ({
      ...item,
      slug: `categories/${item.slug}`,
    })),
    ...tags.map((item) => ({
      ...item,
      slug: `tags/${item.slug}`,
    })),
  ]

  return buildStaticPaths(data, 'blog', { alwaysIncludeItems: 10 })
}

export async function getStaticProps({ params, preview = null }) {
  const blogPost = await fetchContentType(
    'blog-posts',
    `filters[slug]=${params.slug}${preview ? '&publicationState=preview' : ''}`
  )

  if (blogPost?.length === 0 || !blogPost) {
    const blogPostCategory = await fetchContentType(
      'post-categories',
      `filters[slug]=${params.slug}`
    )
    if (
      blogPostCategory.length === 0 ||
      process.env.npm_lifecycle_event === 'build'
    ) {
      return {
        props: {},
        notFound: true,
      }
    }

    return {
      redirect: {
        destination: `/blog/categories/${blogPostCategory[0].slug}`,
      },
    }
  }

  const pageData = await getDataDependencies(delve(blogPost, '0'))
  const blogPostNav = await fetchSingleType(
    'blog-post-nav?populate=post_categories.post_sub_categories'
  )
  const logo = delve(pageData, 'settings.logo', null)
  const blogCardSingleType = await fetchSingleType('blog-post-card')
  const blogSingleType = await fetchSingleType('blog')
  const blogLogo = delve(blogSingleType, 'settings.logo', null)
  const hasCustomLogo = logo || blogLogo
  const settings = delve(pageData, 'settings', {})
  const blogPostNavCategories = spreadStrapiData(blogPostNav?.post_categories)

  return {
    props: {
      pageData: {
        newsletter: delve(blogPostNav, 'newsletter') || null,
        blogPostNavCategories: blogPostNavCategories || null,
        ...pageData,
        ...(hasCustomLogo && {
          settings: {
            ...settings,
            logo: logo || blogLogo,
          },
        }),
      },
      preview,
      url: '/blog',
      blogCardSingleType,
    },
  }
}

export default Article
